import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Banknote, RefreshCw, AlertCircle, CheckCircle2, ShoppingCart, CreditCard } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 900px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
`;

const PageTitle = styled.h1`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary, #333);
`;

const PageSubtitle = styled.p`
  margin: 0.25rem 0 0;
  color: var(--text-secondary, #666);
`;

const ImportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--primary-600, #0284c7);
  color: white;
  border: none;
  border-radius: var(--border-radius-md, 0.5rem);
  font-size: 0.9375rem;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--primary-700, #0369a1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SpinIcon = styled(RefreshCw)<{ $spinning?: boolean }>`
  width: 1.25rem;
  height: 1.25rem;
  ${({ $spinning }) => $spinning && 'animation: incasso-spin 1s linear infinite;'}

  @keyframes incasso-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const Banner = styled.div<{ $variant: 'error' | 'success' }>`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  border-radius: var(--border-radius-md, 0.5rem);
  background: ${({ $variant }) => ($variant === 'error' ? '#fdf1f0' : '#f0f7f1')};
  border: 1px solid ${({ $variant }) => ($variant === 'error' ? '#f2c7c3' : '#c5e0c9')};
  color: ${({ $variant }) => ($variant === 'error' ? '#8a2f27' : '#2d5a35')};

  svg {
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
    margin-top: 0.125rem;
  }
`;

const Card = styled.div`
  background: white;
  border: 1px solid var(--border-light, #e5e7eb);
  border-radius: var(--border-radius-lg, 0.75rem);
  box-shadow: var(--shadow-sm, 0 1px 2px rgba(0, 0, 0, 0.05));
  padding: 1.5rem;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding-bottom: 1rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid var(--border-light, #e5e7eb);
  color: var(--text-primary, #333);

  h2 {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 600;
  }

  svg {
    width: 1.5rem;
    height: 1.5rem;
    color: var(--text-secondary, #666);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-secondary, #666);

  svg {
    width: 3rem;
    height: 3rem;
    color: var(--border-medium, #d1d5db);
    margin: 0 auto 0.75rem;
  }

  h3 {
    margin: 0 0 0.25rem;
    font-size: 1.125rem;
    font-weight: 500;
    color: var(--text-primary, #333);
  }

  p {
    margin: 0;
  }
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 2rem 0;
  color: var(--text-secondary, #666);
`;

const TotalBox = styled.div`
  text-align: center;
  padding: 2rem 1rem;
  background: var(--bg-secondary, #f8f9fa);
  border: 1px solid var(--border-light, #e5e7eb);
  border-radius: var(--border-radius-md, 0.5rem);
`;

const TotalLabel = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-secondary, #666);
  margin-bottom: 0.5rem;
`;

const TotalValue = styled.div`
  font-size: 2.75rem;
  font-weight: 700;
  color: var(--text-primary, #333);
`;

const TotalMeta = styled.div`
  margin-top: 1rem;
  font-size: 0.875rem;
  color: var(--text-secondary, #666);
`;

const MethodsTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 2rem 0 1rem;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary, #333);

  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: var(--text-secondary, #666);
  }
`;

const MethodsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
`;

const MethodCard = styled.div`
  background: white;
  border: 1px solid var(--border-light, #e5e7eb);
  border-radius: var(--border-radius-md, 0.5rem);
  box-shadow: var(--shadow-sm, 0 1px 2px rgba(0, 0, 0, 0.05));
  padding: 1rem;
`;

const MethodLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-secondary, #666);
  margin-bottom: 0.25rem;
`;

const MethodValue = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text-primary, #333);
`;

const NoMethods = styled.div`
  grid-column: 1 / -1;
  font-size: 0.875rem;
  font-style: italic;
  color: var(--text-secondary, #666);
`;

const IncassoPage: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cassaData, setCassaData] = useState<any>(null);
  const [metodi, setMetodi] = useState<any[]>([]);

  const fetchOggi = async () => {
    try {
      setLoading(true);
      const data = await api.getIncassoToday();
      if (data.success) {
        setCassaData(data.cassaGiornaliera);
        setMetodi(data.metodiPagamento || []);
      }
    } catch (err: any) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOggi();
  }, []);

  const handleAnticipaChiusura = async () => {
    if (!window.confirm('Vuoi davvero importare i dati della cassa ora? Questa operazione potrebbe richiedere un minuto.')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      const data = await api.importIncassoNow();
      if (data.success) {
        setSuccess(true);
        fetchOggi();
      } else {
        setError(data.error || 'Errore sconosciuto');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore di connessione o server. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  const displayCassa = cassaData || (metodi.length > 0 ? {
    totale: metodi.reduce((acc, m) => acc + Number(m.totale_vendita || 0), 0),
    updated_at: new Date().toISOString()
  } : null);

  return (
    <PageContainer>
      <PageHeader>
        <div>
          <PageTitle>
            <Banknote size={26} />
            Incasso del Giorno
          </PageTitle>
          <PageSubtitle>
            Visualizza l'incasso e anticipa la chiusura scaricando i dati dalla cassa
          </PageSubtitle>
        </div>

        <ImportButton onClick={handleAnticipaChiusura} disabled={loading}>
          <SpinIcon $spinning={loading} />
          <span>Anticipa Chiusura (Importa Dati)</span>
        </ImportButton>
      </PageHeader>

      {error && (
        <Banner $variant="error">
          <AlertCircle />
          <div>{error}</div>
        </Banner>
      )}

      {success && (
        <Banner $variant="success">
          <CheckCircle2 />
          <div>Dati importati. L'incasso di oggi è stato aggiornato.</div>
        </Banner>
      )}

      <Card>
        <CardHeader>
          <ShoppingCart />
          <h2>Stato Cassa (Oggi)</h2>
        </CardHeader>

        {loading && !displayCassa ? (
          <LoadingText>Caricamento in corso...</LoadingText>
        ) : displayCassa ? (
          <div>
            {user?.ruolo === 'banconista' ? (
              <TotalBox>
                <TotalLabel>Incasso Contanti</TotalLabel>
                <TotalValue>
                  € {Number(metodi.find(m => m.pagamento?.toLowerCase().includes('contant'))?.totale_vendita || 0).toFixed(2)}
                </TotalValue>
                <TotalMeta>
                  Ultimo aggiornamento: {new Date(displayCassa.updated_at).toLocaleString('it-IT')}
                </TotalMeta>
              </TotalBox>
            ) : (
              <div>
                <TotalBox>
                  <TotalLabel>Totale Incasso Registrato</TotalLabel>
                  <TotalValue>€ {Number(displayCassa.totale || 0).toFixed(2)}</TotalValue>
                  <TotalMeta>
                    Ultimo aggiornamento: {new Date(displayCassa.updated_at).toLocaleString('it-IT')}
                  </TotalMeta>
                </TotalBox>

                <MethodsTitle>
                  <CreditCard />
                  Dettaglio per Metodo di Pagamento
                </MethodsTitle>
                <MethodsGrid>
                  {metodi.map((m, idx) => (
                    <MethodCard key={idx}>
                      <MethodLabel>{m.pagamento || 'Sconosciuto'}</MethodLabel>
                      <MethodValue>€ {Number(m.totale_vendita || 0).toFixed(2)}</MethodValue>
                    </MethodCard>
                  ))}
                  {metodi.length === 0 && (
                    <NoMethods>Nessun dettaglio sui pagamenti disponibile.</NoMethods>
                  )}
                </MethodsGrid>
              </div>
            )}
          </div>
        ) : (
          <EmptyState>
            <Banknote />
            <h3>Nessun dato di cassa</h3>
            <p>
              I dati della cassa di oggi non sono ancora stati importati.<br />
              Premi "Anticipa Chiusura" per scaricarli ora, altrimenti verranno importati automaticamente all'1:00 di stanotte.
            </p>
          </EmptyState>
        )}
      </Card>
    </PageContainer>
  );
};

export default IncassoPage;
